import { defineStore } from "pinia";
import { DefaultPaginationItems, defaultTake, type PaginationItems } from "@/models/pagination";
import { DefaultRateGroupModel, type RateGroup, type RateGroupListRequest } from "@/models/rate-group/rate-group";
import {
    createRateGroup,
    getRateGroup,
    getRateGroups,
    updateRateGroup,
    deleteRateGroup,
    listRelationsOfRateGroup,
    getRateGroupSegments
} from '@/services/rate-group/rate-group-service';
import { DefaultModal, type Modal } from "@/models/modal";
import type { RateGroupType } from "@/constants/rate-group-type";
import { RateGroupSegment, RateGroupSegmentTariffInput } from "@/models/rate-group/rate-group-segment";
import {
    ListRelationsOfRateGroupModel,
    ListRelationsOfRateGroupRequest
} from "@/models/rate-group/rate-group";

interface rateGroupState {
    rateGroup: RateGroup;
    rateGroupEditModal: Modal;
    
    rateGroups: PaginationItems<RateGroup>;
    rateGroupListRequest: RateGroupListRequest;

    relationsOfRateGroup: PaginationItems<ListRelationsOfRateGroupModel>;
    listRelationsOfRateGroupRequest: ListRelationsOfRateGroupRequest;
    
    rateGroupSegments: RateGroupSegment[];
    rateGroupSegmentsLoading: boolean;
    
    priceChangeModal: Modal;
    priceChangeInput: RateGroupSegmentTariffInput;
    
    loading: boolean;
}

export const useRateGroupStore = defineStore({
    id: 'rateGroups',
    state: () => ({
        rateGroup: DefaultRateGroupModel(),
        rateGroupEditModal: DefaultModal(),
        
        rateGroups: {
            items: [],
            pageCount: 0,
            totalItems: 0,
        },
        rateGroupListRequest: {
            page: 1,
            take: defaultTake,
            name: undefined,
            type: undefined,
            isActive: true
        },

        relationsOfRateGroup: {
            totalItems: 0,
            items: [],
            pageCount: 0,
        },
        listRelationsOfRateGroupRequest: {
            take: 8,
            page: 1,
            rateGroupId: -1,
            relationName: '',
        },
        
        rateGroupSegments: [],
        rateGroupSegmentsLoading: false,
        priceChangeModal: DefaultModal(),
        priceChangeInput: {
            segmentId: -1,
            fixedCost: '',
            costPerKm: '',
        },
      
        loading: false,
    } as rateGroupState),
    
    actions: {
        async fetchRateGroup(id: number): Promise<void> {
            this.loading = true;
            this.rateGroup = DefaultRateGroupModel();

            const response = await getRateGroup(id);

            if (response.status === 200) {
                this.rateGroup = response.data;
            }

            this.loading = false;
        },
        async fetchRateGroups(rateGroupType: RateGroupType): Promise<void> {
            this.rateGroupListRequest.type = rateGroupType;
            this.loading = true;
            this.rateGroups = DefaultPaginationItems();

            const response = await getRateGroups(this.rateGroupListRequest);

            if (response.status === 200) {
                this.rateGroups = response.data;
            }

            this.loading = false;
        },
        async fetchRateGroupSegments(id?: number): Promise<void> {
            if (!Number.isFinite(id)) {
                this.rateGroupSegments = [];
                return;
            }

            this.rateGroupSegmentsLoading = true;
            try {
                const response = await getRateGroupSegments(id as number);

                if (response.status === 200) {
                    this.rateGroupSegments = response.data;
                } else {
                    this.rateGroupSegments = [];
                }
            }
            finally {
                this.rateGroupSegmentsLoading = false;
            }
        },
        
        openCreateModal() {
            this.rateGroup = DefaultRateGroupModel();            
            this.rateGroupEditModal.visible = true;
        },
        closeCreateModal() {
            this.rateGroupEditModal.visible = false;
            this.rateGroup = DefaultRateGroupModel();
        },

        openPriceChangeModal(){
            this.priceChangeModal.visible = true;
        },
        closePriceChangeModal(){
            this.priceChangeModal.visible = false;
        },
        
        async createOrUpdateRateGroup() {
            this.rateGroupEditModal.loading = true;
            let response = undefined;
            
            if (this.rateGroup?.id) {
                response = await updateRateGroup(this.rateGroup);
            } else {
                response = await createRateGroup(this.rateGroup);
            }

            if (response.status == 200) {
                this.rateGroupEditModal.visible = false;
            }

            this.rateGroupEditModal.loading = false;
            return response;
        },
        async deleteRateGroup(rateGroup: RateGroup) {
            if (rateGroup?.id) {
                await deleteRateGroup(rateGroup.id);
            }
        },
        async fetchRelationsOfRateGroup(rateGroupId: number) {
            this.loading = true;
            this.relationsOfRateGroup = DefaultPaginationItems();

            this.listRelationsOfRateGroupRequest.rateGroupId = rateGroupId;

            const response = await listRelationsOfRateGroup(this.listRelationsOfRateGroupRequest);
            if (response.status == 200) {
                this.relationsOfRateGroup = response.data;
            }
            this.loading = false;
        },
    }
})
